/**
 * Generic iframe utility functions for communicating with parent windows
 * and handling sandboxed iframe restrictions
 */

/**
 * Opens a URL in the parent window context if inside an iframe,
 * otherwise opens directly. This bypasses iframe sandbox restrictions.
 *
 * @param {string} url - The URL to open
 * @param {string} target - The target for window.open (default: '_blank')
 */
export function openInParentWindow(url, target = '_blank') {
  // If we're in an iframe, send message to parent to open URL
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        type: 'open_url',
        url: url,
        target: target,
      },
      '*'
    )
  } else {
    // Fallback: open directly
    window.open(url, target)
  }
}

/**
 * Sends a message to the parent window to update iframe height
 *
 * @param {number} height - The new height for the iframe
 * @param {string} source - Optional source identifier
 */
export function updateIframeHeight(height, source = 'iframe_content') {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        type: 'iframe_height_change',
        height: height,
        source: source,
      },
      '*'
    )
  }
}

/**
 * Sends a custom message to the parent window
 *
 * @param {string} type - The message type
 * @param {object} data - Additional data to send
 */
export function sendToParent(type, data = {}) {
  if (window.parent && window.parent !== window) {
    window.parent.postMessage(
      {
        type: type,
        ...data,
      },
      '*'
    )
  }
}

/**
 * Makes the openInParentWindow function available globally for use in inline scripts
 */
if (typeof window !== 'undefined') {
  window.openInParentWindow = openInParentWindow
}
